import { Box, Collapse, Flex, Image, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Markdown, { ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

import { BASE_PATH, getRanaApiV1 } from "@nens/rana/api-v1";
import { useRect } from "@nens/react/hooks";

import { SIZES, SizingKey } from "../theme/consts/sizing-consts";

import { RanaHeading } from "./rana-heading";

const headingTypes = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

type HeadingProps = React.ClassAttributes<HTMLHeadingElement> &
  React.HTMLAttributes<HTMLHeadingElement> &
  ExtraProps;

const headingTypeToElementMap: Record<
  (typeof headingTypes)[number],
  (props: HeadingProps) => JSX.Element
> = {
  h1: (props) => <RanaHeading type="h2" {...props} />,
  h2: (props) => <RanaHeading type="h3" {...props} />,
  h3: (props) => <RanaHeading type="h4" {...props} />,
  h4: (props) => <RanaHeading type="h5" {...props} />,
  h5: (props) => <RanaHeading type="h6" {...props} />,
  h6: (props) => <Text color="brand.deep_sea" {...props} />,
};

const RanaMarkdownImage = ({ alt, src }: { alt?: string; src?: string }) => {
  const isRanaImage = !!src && src.startsWith("/");
  const { data, isSuccess } = useQuery(
    getRanaApiV1().getQueryOptions<string>({
      queryKey: ["rana-markdown-image", src],
      queryFn: async () => {
        if (!src) throw new Error("Never happens");
        const srcWithoutBasePath = src.replace(BASE_PATH, "");

        const response = (await getRanaApiV1().BaseApi.fetchJson(srcWithoutBasePath)) as {
          url: string;
        };

        return response.url;
      },
      enabled: isRanaImage,
    })
  );

  if (!isRanaImage) {
    return <Image src={src} alt={alt} />;
  }

  if (!isSuccess) return null;

  return <Image src={data} alt={alt} />;
};

const NO_MARGIN_CLASS_NAME = "no-top-margin";

const READ_MORE_BUTTON_H = 16;
const READ_LESS_BUTTON_H = 8;

export function RanaMarkdown({
  markdown,
  readMoreHeight,
}: {
  markdown: string | null | undefined;
  readMoreHeight?: SizingKey;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentHeight = useRect(contentRef).height;

  const startingHeight = readMoreHeight ? SIZES[readMoreHeight] : undefined;

  // FUTURE: These variable definitions are a bit confusing, maybe refactor to make it more clear what is going on

  // Only show the toggle if the content is taller than the readMoreHeight
  const showToggle =
    !!startingHeight &&
    contentHeight > parseInt(startingHeight) + parseInt(SIZES[READ_LESS_BUTTON_H]);

  const [userHasExpendad, setIsExpanded] = useState(false);
  // Always expanded if:
  // 1. The toggle is not shown and the content is loaded
  // 2. The user did not pass a readMoreHeight prop
  const isExpanded = !startingHeight || (contentHeight && !showToggle) ? true : userHasExpendad;

  // Make sure the first child doesn't have a top margin (just using :first-child gives an error even though it works)
  useEffect(() => {
    if (!contentRef.current) return;
    const children = contentRef.current.querySelectorAll("*");
    if (!children.length) return;
    children.forEach((element) => element.classList.remove(NO_MARGIN_CLASS_NAME));
    children[0].classList.add(NO_MARGIN_CLASS_NAME);
  }, [markdown]);

  return (
    <Box
      position="relative"
      as={Collapse}
      in={isExpanded}
      maxH="fit"
      startingHeight={startingHeight}
    >
      <Box
        pb={showToggle ? READ_LESS_BUTTON_H : undefined}
        wordBreak="break-word"
        ref={contentRef}
        sx={{
          "p, hr": { marginTop: `${SIZES[2]} !important` },
          "h1, h2, h3, h4, h5, h6": { marginTop: `${SIZES[6]} !important` },
          [`.${NO_MARGIN_CLASS_NAME}`]: {
            marginTop: "0 !important",
          },

          "ul, ol": { paddingLeft: "1.5em" },

          th: { textAlign: "start" },
        }}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ alt, src }) => <RanaMarkdownImage src={src} alt={alt} />,
            /* Open links in new tab */
            a: ({ children, ...props }) => (
              <a target="_blank" style={{ textDecoration: "underline" }} {...props}>
                {children}
              </a>
            ),
            ...headingTypeToElementMap,
          }}
        >
          {markdown}
        </Markdown>
      </Box>
      {showToggle && (
        <Flex
          alignItems="flex-end"
          h={isExpanded ? READ_LESS_BUTTON_H : READ_MORE_BUTTON_H}
          position="absolute"
          bottom={0}
          right={0}
          left={0}
          bgGradient="linear(to-b, transparent, brand.bg)"
          color="brand.grey.700"
          pointerEvents="none"
        >
          <Box
            pointerEvents="all"
            cursor="pointer"
            textDecoration="underline"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <FormattedMessage id="button.read_less" defaultMessage="Read less" />
            ) : (
              <FormattedMessage id="button.read_more" defaultMessage="Read more" />
            )}
          </Box>
        </Flex>
      )}
    </Box>
  );
}
